import { useState } from 'react'

import { Table } from 'core-components/library/list'
import { ModalDialog } from 'core-components/library/modalDialog'
import { StrategicAlignmentPillar } from 'partner360/features/keyInitiatives'
import { scoreCards } from 'partner360/features/keyInitiatives/configs/config'
import { TRANSLATION_KEYS } from 'partner360/features/keyInitiatives/configs/translations'
import { IScoreCard } from 'partner360/features/keyInitiatives/model/types'
import { ScoreCard } from 'partner360/features/keyInitiatives/ui/common/scoreCard/ScoreCard'
import { formatMoney } from 'partner360/features/keyInitiatives/ui/helpers/formatMoney'
import { QualityPillar } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/components/qualityPillar/QualityPillar'
import { useDescriptionQualityColumns } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useDescriptionQualityColumns'
import { useFinancialTableConfig } from 'partner360/features/keyInitiatives/ui/keyInitiativesQuality/hooks/useFinancialColumns'

import { PlanReviewSection } from '../model/types'
import {
  PLAN_REVIEW_QUALITY_CURRENCY,
  TInitiativesPlanQuality,
} from '../model/usePlanReviewQuality'

import styles from './InitiativesQualityStep.module.scss'

interface IInitiativesQualityStepProps {
  section:
    | PlanReviewSection.FinancialPlanCoverage
    | PlanReviewSection.DescriptionQuality
    | PlanReviewSection.StrategicGrowthAreas
  quality: TInitiativesPlanQuality
}

const noEditor = () => undefined

export const InitiativesQualityStep = ({ section, quality }: IInitiativesQualityStepProps) => {
  const [dialogScoreCard, setDialogScoreCard] = useState<IScoreCard | null>(null)
  const hasDetailsAction =
    section === PlanReviewSection.FinancialPlanCoverage
      ? quality.financialPlanCoverage.initiativesWithIncremental.length > 0
      : section === PlanReviewSection.DescriptionQuality
        ? quality.descriptionQuality.lowScoredCount > 0
        : quality.strategicGrowthAreas.totalCount > 0
  const [isDetailsVisible, setIsDetailsVisible] = useState(hasDetailsAction)
  const { financialTargetGapColumns } = useFinancialTableConfig({
    activeCurrency: PLAN_REVIEW_QUALITY_CURRENCY,
    onEditInitiative: noEditor,
  })
  const { descriptionQualityGapColumns } = useDescriptionQualityColumns({
    onEditInitiative: noEditor,
  })

  const scoreInfoDialog = (
    <ModalDialog
      show={dialogScoreCard !== null}
      onClose={() => setDialogScoreCard(null)}
      contentProps={{ scoreCard: dialogScoreCard }}
      content={ScoreCard}
      size="xs"
    />
  )

  if (section === PlanReviewSection.FinancialPlanCoverage) {
    const pillar = quality.financialPlanCoverage

    return (
      <>
        <QualityPillar
          title={TRANSLATION_KEYS.quality.financialPlanCoverage.title}
          score={pillar.score}
          isNotApplicable={pillar.score === null}
          notApplicableText={TRANSLATION_KEYS.quality.financialPlanCoverage.notApplicable}
          band={pillar.band}
          description={TRANSLATION_KEYS.quality.financialPlanCoverage.description}
          descriptionReplaceParams={[
            { key: '{goalCoverage}', value: String(pillar.goalCoverage) },
            { key: '{incrementalCoverage}', value: String(pillar.incrementalCoverage) },
            {
              key: '{IncrementalPlanSum}',
              value: formatMoney(pillar.incrementalPlanSum, PLAN_REVIEW_QUALITY_CURRENCY),
            },
            {
              key: '{IncrementalInitiativesSum}',
              value: formatMoney(pillar.incrementalInitiativesSum, PLAN_REVIEW_QUALITY_CURRENCY),
            },
          ]}
          successText={
            pillar.allInitiativesHaveTargetsSet
              ? TRANSLATION_KEYS.quality.financialPlanCoverage.success
              : undefined
          }
          actionText={
            pillar.initiativesWithIncremental.length > 0
              ? isDetailsVisible
                ? TRANSLATION_KEYS.quality.actions.hideDetailsFin
                : TRANSLATION_KEYS.quality.actions.showDetailsFin
              : undefined
          }
          actionReplaceParams={[
            {
              key: '{count}',
              value: String(pillar.initiativesWithIncremental.length),
            },
          ]}
          isActionActive={isDetailsVisible}
          onAction={() => setIsDetailsVisible((value) => !value)}
          onHowScore={() => setDialogScoreCard(scoreCards[0])}
        >
          {isDetailsVisible && pillar.initiativesWithIncremental.length > 0 && (
            <div className={styles.detailsTable}>
              <Table
                items={pillar.initiativesWithIncremental}
                columns={financialTargetGapColumns}
                keyField="EntryId"
                header
              />
            </div>
          )}
        </QualityPillar>
        {scoreInfoDialog}
      </>
    )
  }

  if (section === PlanReviewSection.DescriptionQuality) {
    const pillar = quality.descriptionQuality

    return (
      <>
        <QualityPillar
          title={TRANSLATION_KEYS.quality.descriptionQuality.title}
          score={pillar.score}
          band={pillar.band}
          description={TRANSLATION_KEYS.quality.descriptionQuality.description}
          descriptionReplaceParams={[
            { key: '{wellDescribedCount}', value: String(pillar.wellDescribedCount) },
            { key: '{totalCount}', value: String(pillar.totalCount) },
          ]}
          successText={
            pillar.lowScoredCount === 0
              ? TRANSLATION_KEYS.quality.descriptionQuality.success
              : undefined
          }
          actionText={
            pillar.lowScoredCount > 0
              ? isDetailsVisible
                ? TRANSLATION_KEYS.quality.actions.hideDetailsDesc
                : TRANSLATION_KEYS.quality.actions.showDetailsDesc
              : undefined
          }
          actionReplaceParams={[{ key: '{count}', value: String(pillar.lowScoredCount) }]}
          isActionActive={isDetailsVisible}
          onAction={() => setIsDetailsVisible((value) => !value)}
          onHowScore={() => setDialogScoreCard(scoreCards[1])}
        >
          {isDetailsVisible && pillar.lowScoredCount > 0 && (
            <div className={styles.detailsTable}>
              <Table
                items={pillar.lowScoredInitiatives}
                columns={descriptionQualityGapColumns}
                keyField="EntryId"
                header
              />
            </div>
          )}
        </QualityPillar>
        {scoreInfoDialog}
      </>
    )
  }

  return <StrategicAlignmentPillar quality={quality.strategicGrowthAreas} />
}
